import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface ErrorDialogProps {
  open: boolean;
  title?: string;
  message: string;
  /**
   * Backend status code (e.g. `4XX`, `403`). Accepted for backward
   * compatibility with callers that still pass it, but intentionally never
   * rendered — showing a raw code like "Code: 400" to end users isn't
   * useful and was confusing.
   */
  code?: string;
  onClose: () => void;
}

/**
 * Centered modal popup for displaying an error message.
 * Use instead of toast notifications when the user must explicitly acknowledge.
 */
export default function ErrorDialog({ open, title, message, onClose }: ErrorDialogProps) {
  const { t } = useTranslation('common');
  const dialogTitle = title ?? t('somethingWentWrong');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="error-dialog-title"
      className="fixed inset-0 z-[70] flex items-center justify-center px-4"
    >
      <button
        type="button"
        aria-label={t('closeDialogAria')}
        onClick={onClose}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex flex-col items-center px-6 pb-2 pt-6 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-6 w-6"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
              />
            </svg>
          </div>
          <h2 id="error-dialog-title" className="text-lg font-semibold text-gray-900">
            {dialogTitle}
          </h2>
          <p className="mt-2 text-sm text-gray-600">{message}</p>
        </div>
        <div className="px-6 pb-6 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            autoFocus
          >
            {t('ok')}
          </button>
        </div>
      </div>
    </div>
  );
}
